import React, { useState, useEffect, useRef } from 'react';
import { api } from '../../services/api';
import { parseIterationResponse } from '../../services/iterationResponse';

interface Props {
  strategyId?: string;
  onComplete: () => void;
}

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

interface Indicadores {
  presupuesto_disponible: string;
  inversion_competitiva_total: string;
  inversion_total_proyectada: string;
  share_of_investment_proyectado: string;
  ratio_vs_promedio_competitivo: string;
  ratio_vs_lider_competitivo: string;
  nivel_concentracion_competitiva: string;
  nivel_concentracion_media_mix: string;
}

interface MediaMixCanal {
  canal: string;
  porcentaje_recomendado: number;
  rol_estrategico: string;
  modo_inversion: string;
  justificacion: string;
}

interface CanalNoRecomendado {
  canal: string;
  motivo: string;
  condicion_reconsideracion: string;
}

interface DiagnosticState {
  indicadores: Indicadores;
  clasificacion: string;
  justificacion_clasificacion: string;
  principio_estrategico: string;
  media_mix: MediaMixCanal[];
  canales_no_recomendados?: CanalNoRecomendado[];
}

const ModuleZDiagnostic: React.FC<Props> = ({ strategyId, onComplete }) => {
  const [loading, setLoading] = useState(true);
  const [diagnostic, setDiagnostic] = useState<DiagnosticState | null>(null);
  const [processingError, setProcessingError] = useState<string | null>(null);
  const [chatMessage, setChatMessage] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const chatEndRef = useRef<HTMLDivElement>(null);

  const fetchDiagnostic = async (runProcess = false) => {
    setProcessingError(null);
    try {
      if (runProcess) {
        const res = await api.post(`/strategies/${strategyId}/modules/12/process`);
        const data = res.data.module_z_diagnostic;
        if (data) {
          setDiagnostic(typeof data === 'string' ? JSON.parse(data) : data);
        } else {
          throw new Error('El backend no devolvió datos del diagnóstico.');
        }
      } else {
        const res = await api.get(`/strategies/${strategyId}`);
        const data = res.data.module_z_diagnostic;
        if (data) {
          setDiagnostic(typeof data === 'string' ? JSON.parse(data) : data);
        } else {
          await fetchDiagnostic(true);
        }
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.error || err.message || 'Error desconocido';
      console.error('Error en Módulo Z:', errorMsg);
      // BUG FIX: Show error in UI instead of silent alert/jump — prevents auto-forward
      setProcessingError(`Error al procesar el Diagnóstico en Módulo Z: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDiagnostic();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [strategyId]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendChat = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!chatMessage.trim() || loading) return;

    const userMsg = chatMessage;
    setChatMessage('');
    setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setLoading(true);

    try {
      const res = await api.post(`/strategies/${strategyId}/modules/12/chat`, {
        message: userMsg
      });

      const result = parseIterationResponse<any>(res.data);
      if (result.state && Array.isArray(result.state.media_mix)) {
        setDiagnostic((prev: any) => ({ ...(prev || {}), ...result.state }));
      }
      const replyText = result.reply || 'Diagnóstico actualizado';
      setMessages(prev => [...prev, { role: 'assistant', content: replyText }]);
    } catch (err) {
      console.error(err);
      setMessages(prev => [...prev, { role: 'assistant', content: 'Error al actualizar el diagnóstico.' }]);
    } finally {
      setLoading(false);
    }
  };

  if (processingError) {
    return (
      <div style={{ padding: '24px' }}>
        <div style={{
          background: 'rgba(220, 38, 38, 0.08)',
          border: '1px solid rgba(220, 38, 38, 0.4)',
          borderRadius: '8px',
          padding: '24px',
          color: 'var(--text-primary)'
        }}>
          <h3 style={{ color: '#DC2626', fontSize: '16px', marginTop: 0, marginBottom: '12px' }}>
            ⚠️ Error en Módulo Z: Diagnóstico Competitivo
          </h3>
          <p style={{ fontSize: '14px', marginBottom: '20px', fontFamily: 'monospace', background: 'rgba(0,0,0,0.1)', padding: '12px', borderRadius: '4px' }}>
            {processingError}
          </p>
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '20px' }}>
            El sistema no saltará al siguiente módulo automáticamente. Por favor reintenta o contacta soporte con el error mostrado.
          </p>
          <button
            onClick={() => { setLoading(true); fetchDiagnostic(true); }}
            style={{
              background: 'var(--accent)',
              color: '#fff',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '14px'
            }}
          >
            🔄 Reintentar Diagnóstico
          </button>
        </div>
      </div>
    );
  }

  if (loading && !diagnostic) {
    return (
      <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-secondary)' }}>
        Generando diagnóstico estratégico competitivo...
      </div>
    );
  }

  const badgeColor = diagnostic?.clasificacion === 'Marca dominante' 
    ? 'var(--success)' 
    : diagnostic?.clasificacion === 'Marca en paridad competitiva' 
    ? 'var(--accent)' 
    : '#EF4444';

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>Módulo Z: Diagnóstico Competitivo</h2>
      <p style={styles.description}>
        Análisis de viabilidad competitiva e inversión de la marca frente a la categoría.
      </p>

      {diagnostic && (
        <>
          <div style={styles.card}>
            <div style={styles.headerRow}>
              <h3 style={styles.sectionTitle}>Escenario Detectado</h3>
              <span style={{ ...styles.badge, background: badgeColor }}>
                {diagnostic.clasificacion}
              </span>
            </div>
            <p style={styles.bodyText}>{diagnostic.justificacion_clasificacion}</p>
          </div>

          <div style={styles.card}>
            <h3 style={styles.sectionTitle}>Principio Estratégico de Inversión</h3>
            <p style={styles.principioText}>{diagnostic.principio_estrategico}</p>
          </div>

          <div style={styles.card}>
            <h3 style={styles.sectionTitle}>Indicadores Clave</h3>
            <div style={styles.indicatorGrid}>
              {Object.entries(diagnostic.indicadores).map(([key, val]) => (
                <div key={key} style={styles.indicatorCard}>
                  <span style={styles.indicatorLabel}>
                    {key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                  </span>
                  <span style={styles.indicatorValue}>{val}</span>
                </div>
              ))}
            </div>
          </div>

          <div style={styles.card}>
            <h3 style={styles.sectionTitle}>Media Mix Recomendado (Basado en Inversión)</h3>
            <div style={{ overflowX: 'auto', marginTop: '12px' }}>
              <table style={styles.table}>
                <thead>
                  <tr style={styles.thRow}>
                    <th style={styles.th}>Canal</th>
                    <th style={styles.th}>% Recomendado</th>
                    <th style={styles.th}>Rol Estratégico</th>
                    <th style={styles.th}>Modo de Inversión</th>
                    <th style={styles.th}>Justificación</th>
                  </tr>
                </thead>
                <tbody>
                  {diagnostic.media_mix.map((m, idx) => (
                    <tr key={idx} style={styles.tr}>
                      <td style={styles.td}><strong>{m.canal}</strong></td>
                      <td style={styles.td}>{m.porcentaje_recomendado}%</td>
                      <td style={styles.td}>{m.rol_estrategico}</td>
                      <td style={styles.td}>{m.modo_inversion}</td>
                      <td style={styles.td}>{m.justificacion}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {diagnostic.canales_no_recomendados && diagnostic.canales_no_recomendados.length > 0 && (
            <div style={styles.card}>
              <h3 style={styles.sectionTitle}>Canales no recomendados / Baja prioridad</h3>
              <div style={{ overflowX: 'auto', marginTop: '12px' }}>
                <table style={styles.table}>
                  <thead>
                    <tr style={styles.thRow}>
                      <th style={styles.th}>Canal</th>
                      <th style={styles.th}>Motivo</th>
                      <th style={styles.th}>Condición de Reconsideración</th>
                    </tr>
                  </thead>
                  <tbody>
                    {diagnostic.canales_no_recomendados.map((m, idx) => (
                      <tr key={idx} style={styles.tr}>
                        <td style={styles.td}><strong>{m.canal}</strong></td>
                        <td style={styles.td}>{m.motivo}</td>
                        <td style={styles.td}>{m.condicion_reconsideracion}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Chat de Iteración */}
          <div style={styles.chatSection}>
            <h4 style={styles.chatTitle}>¿Deseas ajustar o iterar el diagnóstico de inversión?</h4>
            <div style={styles.chatContainer}>
              <div style={styles.messageList}>
                {messages.length === 0 && (
                  <div style={styles.emptyChat}>
                    Escribe cosas como "sube la inversión en social a 50% y reduce TV", "justifica más el rol de search" o "cambia el principio estratégico para ser más conservador".
                  </div>
                )}
                {messages.map((m, idx) => (
                  <div 
                    key={idx} 
                    style={{
                      ...styles.messageWrapper,
                      justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start'
                    }}
                  >
                    <div 
                      style={{
                        ...styles.messageBubble,
                        background: m.role === 'user' ? 'var(--accent)' : 'var(--bg-hover)',
                        color: m.role === 'user' ? '#fff' : 'var(--text-primary)',
                        alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start'
                      }}
                    >
                      {m.content}
                    </div>
                  </div>
                ))}
                <div ref={chatEndRef} />
              </div>
              <form style={styles.chatForm} onSubmit={handleSendChat}>
                <input 
                  style={styles.chatInput} 
                  value={chatMessage} 
                  onChange={e => setChatMessage(e.target.value)}
                  placeholder="Escribe tu ajuste al diagnóstico..." 
                  disabled={loading}
                />
                <button style={styles.sendBtn} type="submit" disabled={loading || !chatMessage.trim()}>
                  Enviar
                </button>
              </form>
            </div>
          </div>
        </>
      )}

      <div style={{ marginTop: '16px' }}>
        <button style={styles.nextBtn} onClick={onComplete} disabled={loading}>
          Confirmar y continuar al Módulo 6-B
        </button>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: { display: 'flex', flexDirection: 'column', gap: '20px' },
  title: { fontSize: '24px', color: 'var(--text-primary)', fontWeight: 600, margin: 0 },
  description: { fontSize: '15px', color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: '8px' },
  card: {
    background: 'var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: '8px',
    padding: '24px',
    boxShadow: 'var(--shadow)',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  sectionTitle: { fontSize: '16px', fontWeight: 600, color: 'var(--text-primary)', margin: 0 },
  badge: { padding: '6px 12px', borderRadius: '20px', color: '#fff', fontSize: '12px', fontWeight: 600 },
  bodyText: { fontSize: '14px', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.6 },
  principioText: { fontSize: '15px', color: 'var(--accent)', fontWeight: 500, margin: 0, lineHeight: 1.6, fontStyle: 'italic' },
  indicatorGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' },
  indicatorCard: {
    background: 'var(--bg-hover)',
    border: '1px solid var(--border)',
    borderRadius: '6px',
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  indicatorLabel: { fontSize: '11px', color: 'var(--text-muted)', fontWeight: 600, letterSpacing: '0.02em' },
  indicatorValue: { fontSize: '15px', color: 'var(--text-primary)', fontWeight: 600 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  thRow: { background: 'var(--bg-hover)', borderBottom: '1px solid var(--border)' },
  th: { padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)' },
  tr: { borderBottom: '1px solid var(--border)', transition: 'background 0.2s' },
  td: { padding: '12px 16px', color: 'var(--text-primary)', lineHeight: 1.5 },
  chatSection: { marginTop: '24px', borderTop: '1px solid var(--border)', paddingTop: '20px' },
  chatTitle: { margin: '0 0 12px 0', fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)' },
  chatContainer: {
    border: '1px solid var(--border)',
    borderRadius: '8px',
    background: 'var(--bg-card)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    height: '250px'
  },
  messageList: { flex: 1, padding: '16px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '12px' },
  emptyChat: { color: 'var(--text-muted)', fontSize: '13px', textAlign: 'center', marginTop: '40px', fontStyle: 'italic' },
  messageWrapper: { display: 'flex', width: '100%' },
  messageBubble: { padding: '10px 16px', borderRadius: '16px', maxWidth: '75%', fontSize: '14px', lineHeight: 1.4 },
  chatForm: { display: 'flex', borderTop: '1px solid var(--border)', background: 'var(--bg-card)' },
  chatInput: { flex: 1, padding: '12px 16px', border: 'none', background: 'transparent', color: 'var(--text-primary)', fontSize: '14px', outline: 'none' },
  sendBtn: { background: 'var(--accent)', color: '#fff', border: 'none', padding: '0 20px', cursor: 'pointer', fontWeight: 600, fontSize: '14px' },
  nextBtn: {
    background: 'var(--accent)',
    color: '#fff',
    border: 'none',
    padding: '12px 24px',
    borderRadius: '4px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    alignSelf: 'flex-start',
    boxShadow: 'var(--shadow)',
    transition: 'opacity 0.2s'
  }
};

export default ModuleZDiagnostic;
